/**
 * tinode-js: _routeData ends with a synthetic _routeInfo({ what:'read', from, seq, _noForwarding })
 * for every incoming {data}. For trip_automated messages that makes the system sender count
 * toward msgReadCount() → false read ticks on your bubbles. And because _routeInfo stores
 * receipts by assignment, replaying a page of server history drags the peer's read cursor
 * backwards → already-read bubbles revert to "Delivered".
 *
 * Separately: this synthetic read is just an inference ("peer sent something new, so they
 * must have seen everything before it"), not a real recv confirmation. origInfo's handler
 * for 'read'/'recv' always raises recv to match read as a side effect of setting read
 * (`if (user.recv < user.read) user.recv = user.read`). DAX/CAX rarely deliver a genuine,
 * independent recv receipt (backgrounded WebView, push-only), so that bump was the only thing
 * ever moving recv — meaning recv==read at all times and "Delivered" (recv reached, read not
 * yet) can never be observed. Undo the bump for synthetic reads so a real recv receipt is
 * once again the only thing that can move recv ahead of read.
 *
 * Fix: patch Topic.prototype (the base class shared by grp/p2p topics) — NOT TopicMe.prototype.
 * Walk the prototype chain from any topic instance to find where _routeData is an own property.
 */
export function applyTinodeTripAutomatedReadPatch(tinode) {
  if (!tinode || tinode._tripAutomatedReadPatchApplied) return;

  // getMeTopic() creates/returns a TopicMe instance. Walk up the chain to find the base
  // Topic.prototype where _routeData is actually defined as an own property.
  const anyTopic = tinode.getMeTopic();
  if (!anyTopic) return;

  let proto = Object.getPrototypeOf(anyTopic);
  while (proto && !Object.prototype.hasOwnProperty.call(proto, '_routeData')) {
    proto = Object.getPrototypeOf(proto);
  }
  if (!proto || typeof proto._routeData !== 'function' || typeof proto._routeInfo !== 'function') return;

  tinode._tripAutomatedReadPatchApplied = true;

  const origData = proto._routeData;
  const origInfo = proto._routeInfo;

  proto._routeData = function(data) {
    const h = data && data.head;
    const trip = !!(h && (h.trip_automated === true || h.trip_automated === 1));
    const from = data && data.from;
    this._angkasSkipSynthRead =
      trip && from && !this._tinode.isMe(from) ? { from, seq: data.seq | 0 } : null;
    try {
      origData.call(this, data);
    } finally {
      this._angkasSkipSynthRead = null;
    }
  };

  proto._routeInfo = function(info) {
    const skip = this._angkasSkipSynthRead;
    if (
      skip &&
      info &&
      info._noForwarding &&
      info.what === 'read' &&
      info.from === skip.from &&
      (info.seq | 0) === skip.seq
    ) {
      return;
    }

    const peer = info?._noForwarding && info?.what === 'read' ? this._users?.[info.from] : null;
    if (peer) {
      // Replayed history: the cursor is already past this message, so the receipt can only drag
      // `read` backwards. _updateMyReadRecv ignores _noForwarding receipts, so dropping loses nothing.
      if ((info.seq | 0) < (peer.read | 0)) {
        return;
      }
      // origInfo's 'read' branch only ever raises recv to match read, never lowers it, so
      // restoring unconditionally in `finally` undoes exactly that bump (and nothing else)
      // even if a callback triggered by origInfo (_refreshContact, onInfo) throws.
      const recvBefore = peer.recv;
      try {
        return origInfo.call(this, info);
      } finally {
        peer.recv = recvBefore;
      }
    }

    return origInfo.call(this, info);
  };
}
